import { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card'
import { cn } from '../../utils/cn'

interface MetricCardProps {
  title: string
  value: string | number
  description?: string
  icon?: LucideIcon
  trend?: {
    value: number
    label: string
    direction?: 'up' | 'down' | 'neutral'
  }
  variant?: 'default' | 'accent' | 'success' | 'warning'
  className?: string
}

export const MetricCard = ({
  title,
  value,
  description,
  icon: Icon,
  trend,
  variant = 'default',
  className
}: MetricCardProps) => {
  const variantClasses = {
    default: 'border-l-4 border-l-primary',
    accent: 'border-l-4 border-l-accent',
    success: 'border-l-4 border-l-emerald-500',
    warning: 'border-l-4 border-l-amber-500'
  }

  const trendColor = trend?.direction === 'up'
    ? 'text-emerald-600'
    : trend?.direction === 'down'
      ? 'text-rose-600'
      : 'text-muted-foreground'

  return (
    <Card className={cn('relative overflow-hidden', variantClasses[variant], className)}>
      {Icon && (
        <div className="absolute right-4 top-4 opacity-10">
          <Icon className="h-16 w-16" strokeWidth={1} />
        </div>
      )}

      <CardHeader className="pb-3">
        <CardDescription className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" />}
          {title}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        <CardTitle className="text-4xl font-bold tracking-tight">{value}</CardTitle>

        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}

        {trend && (
          <div className="flex items-center gap-2 pt-2 border-t border-border/50">
            <span className={cn('font-mono text-sm font-bold', trendColor)}>
              {trend.direction === 'up' && '↑ '}
              {trend.direction === 'down' && '↓ '}
              {trend.value > 0 ? '+' : ''}{trend.value}%
            </span>
            <span className="text-xs text-muted-foreground">{trend.label}</span>
          </div>
        )}
      </CardContent>

      {/* Elevation-style bottom gradient */}
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-current to-transparent opacity-30" style={{
        color: variant === 'accent' ? 'hsl(var(--accent))' :
               variant === 'success' ? 'rgb(16, 185, 129)' :
               variant === 'warning' ? 'rgb(245, 158, 11)' :
               'hsl(var(--primary))'
      }}></div>
    </Card>
  )
}

// Elevation Profile Chart Component
interface ElevationPoint {
  distance: number
  elevation: number
}

interface ElevationProfileProps {
  data: ElevationPoint[]
  className?: string
}

export const ElevationProfile = ({ data, className }: ElevationProfileProps) => {
  if (data.length === 0) {
    return null
  }

  const maxElevation = Math.max(...data.map(p => p.elevation))
  const minElevation = Math.min(...data.map(p => p.elevation))
  const elevationRange = maxElevation - minElevation
  const maxDistance = Math.max(...data.map(p => p.distance))

  // Create SVG path
  const points = data.map((point, i) => {
    const x = (point.distance / maxDistance) * 100
    const y = 100 - ((point.elevation - minElevation) / elevationRange) * 100
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
  }).join(' ')

  // Create area fill path
  const areaPath = `${points} L 100 100 L 0 100 Z`

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 20 L9 14 L15 18 L21 12" />
            </svg>
          </span>
          Elevation Profile
        </CardTitle>
        <CardDescription>
          {minElevation.toFixed(0)}m to {maxElevation.toFixed(0)}m • {maxDistance.toFixed(1)}km total
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative h-48 w-full">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
            {/* Grid lines */}
            {[...Array(5)].map((_, i) => (
              <line
                key={i}
                x1="0"
                y1={i * 25}
                x2="100"
                y2={i * 25}
                stroke="hsl(var(--border))"
                strokeWidth="0.2"
                opacity="0.5"
              />
            ))}

            {/* Area fill */}
            <path
              d={areaPath}
              fill="url(#elevationGradient)"
              opacity="0.3"
            />

            {/* Line */}
            <path
              d={points}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Gradient definition */}
            <defs>
              <linearGradient id="elevationGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-3 gap-4 rounded-lg bg-muted/30 p-3">
          <div>
            <div className="text-xs text-muted-foreground">Gain</div>
            <div className="font-mono text-lg font-bold text-foreground">
              +{(maxElevation - minElevation).toFixed(0)}m
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Peak</div>
            <div className="font-mono text-lg font-bold text-foreground">
              {maxElevation.toFixed(0)}m
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Distance</div>
            <div className="font-mono text-lg font-bold text-foreground">
              {maxDistance.toFixed(1)}km
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
